/**
 * Hook to handle keyboard visibility and height on mobile devices
 * Uses Capacitor Keyboard API for native apps and visualViewport for web
 *
 * Android strategy: adjustNothing + edge-to-edge. Viewport does NOT resize.
 * Primary: Java WindowInsetsAnimation.Callback dispatches android-keyboard events.
 * Fallback: Poll AndroidKeyboard.getHeight() JS interface every 200ms while
 * an input is focused. This covers devices/scenarios where the callback fails.
 *
 * iOS strategy: Capacitor keyboard events work reliably.
 */

import { Capacitor } from "@capacitor/core";
import { Keyboard, type KeyboardInfo } from "@capacitor/keyboard";
import { useState, useEffect } from "react";

interface KeyboardState {
  isVisible: boolean;
  height: number;
}

declare global {
  interface Window {
    AndroidKeyboard?: { getHeight: () => number };
  }
}

interface ApplyKeyboardHeightOptions {
  /**
   * When false, only updates --keyboard-height (for scroll-margin, drawers, etc.)
   * without shrinking body/#root. Mobile browsers already resize the visual
   * viewport; shrinking the layout viewport as well causes blank space and flicker.
   */
  shrinkLayout?: boolean;
}

/** Apply keyboard height to document */
function applyKeyboardHeight(
  height: number,
  { shrinkLayout = true }: ApplyKeyboardHeightOptions = {},
) {
  const isVisible = height > 0;
  document.documentElement.style.setProperty(
    "--keyboard-height",
    isVisible ? `${height}px` : "0px",
  );
  if (shrinkLayout) {
    if (isVisible) {
      document.documentElement.classList.add("keyboard-visible");
    } else {
      document.documentElement.classList.remove("keyboard-visible");
    }
  }
}

const WEB_KEYBOARD_SHOW_THRESHOLD = 100;
const WEB_KEYBOARD_HIDE_THRESHOLD = 40;

/** Hysteresis avoids flicker while the mobile browser keyboard animates open. */
function getStableWebKeyboardHeight(rawHeight: number, wasVisible: boolean) {
  if (wasVisible) {
    return rawHeight < WEB_KEYBOARD_HIDE_THRESHOLD ? 0 : rawHeight;
  }
  return rawHeight > WEB_KEYBOARD_SHOW_THRESHOLD ? rawHeight : 0;
}

export function useKeyboard(): KeyboardState {
  const [state, setState] = useState<KeyboardState>({
    isVisible: false,
    height: 0,
  });

  useEffect(() => {
    const isNative = Capacitor.isNativePlatform();
    const isAndroid = Capacitor.getPlatform() === "android";

    if (isNative) {
      // Capacitor keyboard events (works on iOS, may not fire on Android with adjustNothing)
      const showListener = Keyboard.addListener(
        "keyboardWillShow",
        (info: KeyboardInfo) => {
          setState({ isVisible: true, height: info.keyboardHeight });
          applyKeyboardHeight(info.keyboardHeight);
        },
      );

      const hideListener = Keyboard.addListener("keyboardWillHide", () => {
        setState({ isVisible: false, height: 0 });
        applyKeyboardHeight(0);
      });

      // Android: listen for native WindowInsets events from Java
      const handleAndroidKeyboard = isAndroid
        ? (e: Event) => {
            const { height } = (e as CustomEvent).detail;
            setState({ isVisible: height > 0, height });
            applyKeyboardHeight(height);
          }
        : null;

      if (handleAndroidKeyboard) {
        window.addEventListener("android-keyboard", handleAndroidKeyboard);
      }

      // Android fallback: poll AndroidKeyboard.getHeight() JS interface.
      // The WindowInsetsAnimation callback + evaluateJavascript chain can be
      // unreliable on some devices. Polling the JS interface is a direct query
      // of WindowInsets state, bypassing the event chain entirely.
      let pollTimer: ReturnType<typeof setInterval> | null = null;
      if (isAndroid) {
        let lastPolledHeight = -1;
        pollTimer = setInterval(() => {
          const height = window.AndroidKeyboard?.getHeight?.() ?? -1;
          if (height === -1) return; // Interface not ready
          if (height === lastPolledHeight) return;
          lastPolledHeight = height;
          setState({ isVisible: height > 0, height });
          applyKeyboardHeight(height);
        }, 200);
      }

      return () => {
        showListener.then(handle => handle.remove());
        hideListener.then(handle => handle.remove());
        if (handleAndroidKeyboard) {
          window.removeEventListener("android-keyboard", handleAndroidKeyboard);
        }
        if (pollTimer) clearInterval(pollTimer);
      };
    } else {
      // Web: detect keyboard via visualViewport API.
      const viewport = window.visualViewport;
      if (!viewport) return;

      let debounceTimer: ReturnType<typeof setTimeout>;
      let wasVisible = false;

      const handleResize = () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          const rawHeight = window.innerHeight - viewport.height;
          const height = getStableWebKeyboardHeight(rawHeight, wasVisible);
          const isVisible = height > 0;
          wasVisible = isVisible;

          setState({ isVisible, height });
          applyKeyboardHeight(height, { shrinkLayout: false });
        }, 50);
      };

      viewport.addEventListener("resize", handleResize);
      return () => {
        clearTimeout(debounceTimer);
        viewport.removeEventListener("resize", handleResize);
      };
    }
  }, []);

  return state;
}

/**
 * Initialize keyboard CSS variable on app start
 * Call this once in your app root
 */
export function initKeyboardHandler(): void {
  document.documentElement.style.setProperty("--keyboard-height", "0px");

  const isNative = Capacitor.isNativePlatform();
  const isAndroid = Capacitor.getPlatform() === "android";

  if (isNative) {
    if (isAndroid) {
      // Android: adjustNothing + edge-to-edge means nothing resizes.
      // Use Capacitor events, Java events, AND polling as fallback.
      Keyboard.addListener("keyboardWillShow", (info: KeyboardInfo) => {
        applyKeyboardHeight(info.keyboardHeight);
      });
      Keyboard.addListener("keyboardWillHide", () => {
        applyKeyboardHeight(0);
      });

      window.addEventListener("android-keyboard", (e: Event) => {
        const { height } = (e as CustomEvent).detail;
        applyKeyboardHeight(height);
      });

      // Poll AndroidKeyboard JS interface as fallback
      let lastPolledHeight = 0;
      setInterval(() => {
        const height = window.AndroidKeyboard?.getHeight?.() ?? -1;
        if (height === -1) return;
        if (height !== lastPolledHeight) {
          lastPolledHeight = height;
          applyKeyboardHeight(height);
        }
      }, 200);
    } else {
      // iOS: Capacitor events work reliably
      Keyboard.addListener("keyboardWillShow", (info: KeyboardInfo) => {
        applyKeyboardHeight(info.keyboardHeight);

        setTimeout(() => {
          const activeElement = document.activeElement as HTMLElement;
          if (
            activeElement &&
            (activeElement.tagName === "INPUT" ||
              activeElement.tagName === "TEXTAREA" ||
              activeElement.contentEditable === "true")
          ) {
            activeElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 100);
      });

      Keyboard.addListener("keyboardWillHide", () => {
        applyKeyboardHeight(0);
      });
    }
  } else {
    // Web: detect keyboard via visualViewport API.
    const viewport = window.visualViewport;
    if (viewport) {
      let debounceTimer: ReturnType<typeof setTimeout>;
      let wasVisible = false;

      const handleViewportResize = () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          const rawHeight = window.innerHeight - viewport.height;
          const height = getStableWebKeyboardHeight(rawHeight, wasVisible);
          wasVisible = height > 0;
          applyKeyboardHeight(height, { shrinkLayout: false });
        }, 50);
      };

      viewport.addEventListener("resize", handleViewportResize);
    }
  }
}
